import cx from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getClientSettings } from '@wg/web2clientapi/browser/getClientSettings';
import { updateBrowserControlState } from '@wg/web2clientapi/browser/updateBrowserControlState';
import ErrorLoad from '@wg/wows-react-uikit/components/ErrorLoad/ErrorLoad';
import { OnboardingProvider } from '@wg/wows-react-uikit/features/onboarding/provider/onboardingProvider';

import { root } from '~/preloaded';
import { fetchOnboardingCompletedSteps, fetchOnboardingCompleteSteps } from '~/api/onboarding';
import { onboardingStepsList } from '~/const/onboarding';
import { isInGame, isWCFRequired } from '~/constants';
import { logPageView } from '~/helpers/analytics';
import { t } from '~/helpers/localization';
import useGlobalControlStateManager from '~/hooks/useGlobalControlStateManager';
import useLocationChange from '~/hooks/useLocationChange';
import useMount from '~/hooks/useMount';
import { usePoshtaInit, usePoshtaMarkersClientSync } from '~/hooks/usePoshta';
import useRouteHandlePropChange from '~/hooks/useRouteHandlePropChange';
import { RefKeys, refManager } from '~/managers/refManager';
import NavigationGuard from '~/router/guards/NavigationGuard';
import { ROUTES_MAP } from '~/router/routes';
import { useAppDispatch, useAppSelector } from '~/store';
import {
  currentAccountActiveInvitesCountSelector,
  currentClanIdSelector,
  isLoggedInSelector,
} from '~/store/selectors/currentAccountSelector';
import { navigationSelector } from '~/store/selectors/navigationSelectors';
import { currentAccountActions } from '~/store/slices/currentAccountSlice';

import { IsFromSearchContext } from '~/Providers/IsFromSearch';
import { ReturnUrlContext } from '~/Providers/ReturnUrl';
import DialogsManager from '~/Components/Managers/DialogsManager/DialogsManager';
import NavMenu from '~/Components/NavMenu/NavMenu';
import ViewBackground from '~/Components/ViewBackground/ViewBackground';

import styles from './DefaultLayout.module.scss';

import type { Nullable } from '~/types/generics';

const bodyClassName = isInGame ? 'ingame' : 'not-ingame';

const DocumentTitleManager = () => {
  const navigationContext = useAppSelector(navigationSelector);

  useRouteHandlePropChange('title', (navigationSelector) => {
    document.title = navigationSelector?.(navigationContext) || t('Кланы');
  });

  return null;
};

const DefaultLayout = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeInvitesCount = useAppSelector(currentAccountActiveInvitesCountSelector);
  const currentClanId = useAppSelector(currentClanIdSelector);
  const isLoggedIn = useAppSelector(isLoggedInSelector);

  const appRef = refManager.getRef(RefKeys.APP);

  const isOpenedFromSearch = pathname.includes('clan-profile');
  const isRoot = pathname === root;

  const [returnUrl, setReturnUrl] = useState<string | null>(null);

  useGlobalControlStateManager(returnUrl);

  useLocationChange(() => {
    logPageView();

    if (isWCFRequired && appRef.current) {
      appRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });

  const [onboardingCompletedSteps, setOnboardingCompletedSteps] = useState<Nullable<string[]>>(null);

  const getUserGraphicsSettings = useCallback(async () => {
    try {
      const response = await getClientSettings();
      if (response.data) {
        dispatch(currentAccountActions.setClientSettings(response.data));
      }
    } catch (e) {
      console.error('Unable to get client data', e);
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    void fetchOnboardingCompletedSteps().then((steps) => {
      if (!steps) {
        return;
      }
      setOnboardingCompletedSteps(steps);
    });
  }, [isLoggedIn]);

  usePoshtaInit();
  usePoshtaMarkersClientSync();

  useMount(() => {
    document.body.className = bodyClassName;

    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl) {
      setReturnUrl(returnUrl);
    }

    if (isInGame) {
      void getUserGraphicsSettings();
    }

    if (isRoot && !currentClanId && activeInvitesCount) {
      navigate(ROUTES_MAP.REQUESTS, { replace: true });
    }
  });

  const completeSteps = useCallback(
    (stepsNames: string[]) => {
      if (!isLoggedIn) {
        return;
      }
      void fetchOnboardingCompleteSteps(stepsNames);
    },
    [isLoggedIn],
  );

  const appContainer = useRef<HTMLElement>(document.getElementById('app'));
  const contentContainer = useRef<HTMLElement>(null);

  const [showShadowTop, setShowShadowTop] = useState(false);
  const [shadowWidth, setShadowWidth] = useState(0);
  const [shadowLeft, setShadowLeft] = useState(0);

  useMount(() => {
    updateShadows();

    appContainer.current?.addEventListener('scroll', updateShadows);
    window.addEventListener('resize', updateShadows);

    return () => {
      appContainer.current?.removeEventListener('scroll', updateShadows);
      window.removeEventListener('resize', updateShadows);
    };
  });

  const updateShadows = () => {
    if (!appContainer.current) {
      return;
    }

    setShowShadowTop(appContainer.current.scrollTop > 0);

    const contentRect = contentContainer.current?.getBoundingClientRect();
    if (contentRect) {
      setShadowWidth(contentRect.width + 16);
      setShadowLeft(contentRect.left - 8);
    }
  };

  const renderError = useCallback(() => {
    void updateBrowserControlState(false);

    return (
      <ErrorLoad
        isFlat
        key="error-load"
        message={t('Произошла ошибка. Повторите попытку позже')}
        onReloadClick={() => window.location.reload()}
      />
    );
  }, []);

  return (
    <OnboardingProvider
      steps={onboardingStepsList}
      completedSteps={onboardingCompletedSteps}
      completeSteps={completeSteps}
    >
      <IsFromSearchContext.Provider value={isOpenedFromSearch}>
        <ReturnUrlContext.Provider value={returnUrl}>
          <ErrorBoundary fallbackRender={renderError}>
            <ViewBackground />
            <main className={styles.main}>
              <NavMenu />
              <section className={styles.section} ref={contentContainer}>
                <div
                  className={cx(styles.shadowTop, {
                    [styles.isVisible]: showShadowTop,
                  })}
                  style={{
                    left: shadowLeft,
                    width: shadowWidth,
                  }}
                />
                <NavigationGuard>
                  <Outlet />
                </NavigationGuard>
              </section>
            </main>
            {!isInGame && <DocumentTitleManager />}
          </ErrorBoundary>
          <DialogsManager />
        </ReturnUrlContext.Provider>
      </IsFromSearchContext.Provider>
    </OnboardingProvider>
  );
};

export default DefaultLayout;
